import { getChatCompletion } from './aiIntegrations/chatCompletion';

const AI_PROVIDER = 'OPEN_AI';
const AI_MODEL = 'gpt-5';

/**
 * Analyze occupancy trends and predict future vacancy rates
 */
export async function analyzeOccupancyTrends(data) {
  try {
    const { occupiedUnits, totalUnits, historicalData, expiringLeases } = data;
    
    const prompt = `You are a commercial real estate analytics AI. Analyze the following occupancy data and provide predictions:

Current Status:
- Occupied Units: ${occupiedUnits}
- Total Units: ${totalUnits}
- Current Occupancy Rate: ${((occupiedUnits / totalUnits) * 100)?.toFixed(1)}%
- Expiring Leases (Next 3 Months): ${expiringLeases}

Historical Data: ${JSON.stringify(historicalData)}

Provide:
1. Predicted occupancy rate for next 3 months
2. Risk level (low/medium/high)
3. Key insights (max 2 sentences)
4. Recommended actions (max 2 items)

Respond in JSON format only.`;

    const response = await getChatCompletion(
      AI_PROVIDER,
      AI_MODEL,
      [
        { role: 'system', content: 'You are a commercial real estate analytics expert. Always respond with valid JSON.' },
        { role: 'user', content: prompt }
      ],
      {
        max_completion_tokens: 800,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'OccupancyAnalysis',
            schema: {
              type: 'object',
              properties: {
                predictedOccupancyRate: { type: 'number' },
                riskLevel: { type: 'string', enum: ['low', 'medium', 'high'] },
                insights: { type: 'string' },
                recommendations: {
                  type: 'array',
                  items: { type: 'string' }
                }
              },
              required: ['predictedOccupancyRate', 'riskLevel', 'insights', 'recommendations'],
              additionalProperties: false
            },
            strict: true
          }
        }
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    if (!content || typeof content !== 'string') {
      throw new Error('Invalid response from AI service');
    }
    return JSON.parse(content);
  } catch (error) {
    console.error('Occupancy analysis error:', error);
    throw error;
  }
}

/**
 * Calculate churn probability for tenants based on multiple factors
 */
export async function calculateChurnRisk(tenants) {
  try {
    const tenantSummary = tenants?.map(t => ({
      id: t?.id,
      name: t?.companyName || t?.name,
      monthsRemaining: t?.monthsRemaining,
      revenue: t?.monthlyRent || t?.revenue,
      paymentHistory: t?.paymentDelays || 0,
      renewalHistory: t?.renewalCount || 0
    }));

    const prompt = `Analyze tenant churn risk for the following tenants:

${JSON.stringify(tenantSummary, null, 2)}

For each tenant, calculate:
1. Churn probability (0-100%)
2. Risk category (low/medium/high/critical)
3. Primary risk factors
4. Retention strategy

Respond in JSON format with an array of tenant analyses.`;

    const response = await getChatCompletion(
      AI_PROVIDER,
      AI_MODEL,
      [
        { role: 'system', content: 'You are a tenant retention analytics expert. Analyze churn risk based on lease duration, payment history, and renewal patterns. Always respond with valid JSON.' },
        { role: 'user', content: prompt }
      ],
      {
        max_completion_tokens: 1000,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'ChurnRiskAnalysis',
            schema: {
              type: 'object',
              properties: {
                tenantAnalyses: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      tenantId: { type: 'string' },
                      churnProbability: { type: 'number' },
                      riskFactors: {
                        type: 'array',
                        items: { type: 'string' }
                      },
                      retentionStrategy: { type: 'string' }
                    },
                    required: ['tenantId', 'churnProbability', 'riskFactors', 'retentionStrategy'],
                    additionalProperties: false
                  }
                }
              },
              required: ['tenantAnalyses'],
              additionalProperties: false
            },
            strict: true
          }
        }
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    if (!content || typeof content !== 'string') {
      console.error('Invalid AI response structure:', response);
      throw new Error('AI service returned invalid response format');
    }
    return JSON.parse(content);
  } catch (error) {
    console.error('Churn risk calculation error:', error);
    throw error;
  }
}

/**
 * Generate dynamic pricing suggestions based on market conditions
 */
export async function generatePricingRecommendations(data) {
  try {
    const { vacantUnits, marketData, competitorPricing, seasonalTrends } = data;

    const prompt = `Analyze pricing strategy for commercial real estate:

Vacant Units: ${JSON.stringify(vacantUnits)}
Market Conditions: ${JSON.stringify(marketData)}
Competitor Pricing: ${JSON.stringify(competitorPricing)}
Seasonal Trends: ${JSON.stringify(seasonalTrends)}

Provide pricing recommendations for each vacant unit including:
1. Suggested price adjustment (percentage)
2. Reasoning
3. Expected impact on occupancy
4. Optimal timing for implementation

Respond in JSON format.`;

    const response = await getChatCompletion(
      AI_PROVIDER,
      AI_MODEL,
      [
        { role: 'system', content: 'You are a commercial real estate pricing strategist. Provide data-driven pricing recommendations. Always respond with valid JSON.' },
        { role: 'user', content: prompt }
      ],
      {
        max_completion_tokens: 1000,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'PricingRecommendations',
            schema: {
              type: 'object',
              properties: {
                recommendations: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      unitId: { type: 'string' },
                      currentPrice: { type: 'number' },
                      suggestedPrice: { type: 'number' },
                      adjustmentPercentage: { type: 'number' },
                      reasoning: { type: 'string' },
                      expectedImpact: { type: 'string' },
                      timing: { type: 'string' }
                    },
                    required: ['unitId', 'currentPrice', 'suggestedPrice', 'adjustmentPercentage', 'reasoning', 'expectedImpact', 'timing'],
                    additionalProperties: false
                  }
                },
                overallStrategy: { type: 'string' }
              },
              required: ['recommendations', 'overallStrategy'],
              additionalProperties: false
            },
            strict: true
          }
        }
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    if (!content || typeof content !== 'string') {
      throw new Error('Invalid response from AI service');
    }
    return JSON.parse(content);
  } catch (error) {
    console.error('Pricing recommendations error:', error);
    throw error;
  }
}

/**
 * Analyze revenue trends and forecast future performance
 */
export async function analyzeRevenueTrends(revenueData) {
  try {
    const prompt = `Analyze revenue trends for commercial real estate centers:

${JSON.stringify(revenueData, null, 2)}

Provide:
1. Revenue growth rate (percentage)
2. Trend direction (increasing/stable/decreasing)
3. Key insights (max 3 sentences)
4. Revenue forecast for next quarter
5. Risk factors
6. Growth opportunities

Respond in JSON format.`;

    const response = await getChatCompletion(
      AI_PROVIDER,
      AI_MODEL,
      [
        { role: 'system', content: 'You are a financial analyst specializing in commercial real estate. Provide accurate revenue analysis and forecasts. Always respond with valid JSON.' },
        { role: 'user', content: prompt }
      ],
      {
        max_completion_tokens: 1000,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'RevenueAnalysis',
            schema: {
              type: 'object',
              properties: {
                growthRate: { type: 'number' },
                trend: { type: 'string', enum: ['increasing', 'stable', 'decreasing'] },
                insights: { type: 'string' },
                quarterlyForecast: { type: 'number' },
                riskFactors: {
                  type: 'array',
                  items: { type: 'string' }
                },
                opportunities: {
                  type: 'array',
                  items: { type: 'string' }
                }
              },
              required: ['growthRate', 'trend', 'insights', 'quarterlyForecast', 'riskFactors', 'opportunities'],
              additionalProperties: false
            },
            strict: true
          }
        }
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    if (!content || typeof content !== 'string') {
      throw new Error('Invalid response from AI service');
    }
    return JSON.parse(content);
  } catch (error) {
    console.error('Revenue analysis error:', error);
    throw error;
  }
}

/**
 * Generate comprehensive business insights from dashboard metrics
 */
export async function generateDashboardInsights(metrics) {
  try {
    const { occupancyRate, revenue, leads, expiringLeases, vacantUnits } = metrics;

    const prompt = `Analyze these commercial real estate KPIs and provide executive insights:

Occupancy Rate: ${occupancyRate}%
Total Revenue: $${revenue}
Active Leads: ${leads}
Expiring Leases (90 days): ${expiringLeases}
Vacant Units: ${vacantUnits}

Provide:
1. Overall health score (0-100)
2. Top 3 priorities for management
3. Performance summary (2-3 sentences)
4. Urgent actions needed

Respond in JSON format.`;

    const response = await getChatCompletion(
      AI_PROVIDER,
      AI_MODEL,
      [
        { role: 'system', content: 'You are a commercial real estate operations consultant. Provide actionable insights for property managers. Always respond with valid JSON.' },
        { role: 'user', content: prompt }
      ],
      {
        max_completion_tokens: 800,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'DashboardInsights',
            schema: {
              type: 'object',
              properties: {
                healthScore: { type: 'number' },
                priorities: {
                  type: 'array',
                  items: { type: 'string' }
                },
                summary: { type: 'string' },
                urgentActions: {
                  type: 'array',
                  items: { type: 'string' }
                }
              },
              required: ['healthScore', 'priorities', 'summary', 'urgentActions'],
              additionalProperties: false
            },
            strict: true
          }
        }
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    if (!content || typeof content !== 'string') {
      throw new Error('Invalid response from AI service');
    }
    return JSON.parse(content);
  } catch (error) {
    console.error('Dashboard insights error:', error);
    throw error;
  }
}

/**
 * Analyze lead conversion patterns and suggest improvements
 */
export async function analyzeLeadPerformance(leadData) {
  try {
    const prompt = `Analyze lead pipeline performance:

${JSON.stringify(leadData, null, 2)}

Provide:
1. Conversion rate by stage
2. Bottleneck identification
3. Improvement suggestions (max 3)
4. Expected impact of improvements

Respond in JSON format.`;

    const response = await getChatCompletion(
      AI_PROVIDER,
      AI_MODEL,
      [
        { role: 'system', content: 'You are a sales analytics expert for commercial real estate. Identify conversion bottlenecks and optimization opportunities. Always respond with valid JSON.' },
        { role: 'user', content: prompt }
      ],
      {
        max_completion_tokens: 900,
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'LeadAnalysis',
            schema: {
              type: 'object',
              properties: {
                conversionRates: {
                  type: 'object',
                  properties: {
                    inquiry: { type: 'number' },
                    siteVisit: { type: 'number' },
                    proposal: { type: 'number' },
                    negotiation: { type: 'number' }
                  },
                  required: ['inquiry', 'siteVisit', 'proposal', 'negotiation'],
                  additionalProperties: false
                },
                bottlenecks: {
                  type: 'array',
                  items: { type: 'string' }
                },
                improvements: {
                  type: 'array',
                  items: { type: 'string' }
                },
                expectedImpact: { type: 'string' }
              },
              required: ['conversionRates', 'bottlenecks', 'improvements', 'expectedImpact'],
              additionalProperties: false
            },
            strict: true
          }
        }
      }
    );

    const content = response?.choices?.[0]?.message?.content;
    if (!content || typeof content !== 'string') {
      throw new Error('Invalid response from AI service');
    }
    return JSON.parse(content);
  } catch (error) {
    console.error('Lead analysis error:', error);
    throw error;
  }
}